import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Botao, classesDeBotao } from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import { carregarCardDaEmpresa, criarCard } from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';

/**
 * "Vincular ao CRM" no cadastro da empresa — ou "Abrir no CRM", um por quadro
 * em que ela já está.
 *
 * Só admin vê: o CRM é da operadora (decisão de 30/09/2026), e a rota
 * responderia 403 a qualquer outro. Empresa desativada não entra — o servidor
 * recusaria —, então o botão nem aparece para ela.
 *
 * Vincular põe a empresa na primeira coluna do quadro padrão; outro quadro
 * se escolhe no próprio pipeline ("+ Adicionar empresa").
 */
export function BotaoDoCrm(props: {
  readonly businessId: string;
  readonly ativa: boolean;
}): JSX.Element | null {
  const identidade = useIdentidade();
  const queryClient = useQueryClient();
  const admin = identidade?.isAdmin === true;

  const consulta = useQuery({
    queryKey: ['crm', 'empresa', props.businessId],
    queryFn: () => carregarCardDaEmpresa(props.businessId),
    enabled: admin && props.ativa,
  });
  const vincular = useMutation({
    mutationFn: () => criarCard(props.businessId),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  if (!admin || !props.ativa || consulta.data === undefined) return null;

  const { cards } = consulta.data;
  return (
    <div className="flex flex-col items-end gap-2">
      {cards.length === 0 ? (
        <Botao
          variante="secundario"
          tamanho="pequeno"
          carregando={vincular.isPending}
          onClick={() => vincular.mutate()}
        >
          Vincular ao CRM
        </Botao>
      ) : (
        cards.map((card) => (
          <Link
            key={card.id}
            to={`/crm/pipeline?quadro=${card.quadroId}&card=${card.id}`}
            className={classesDeBotao('secundario', 'pequeno')}
            title={`No quadro "${card.quadroNome}", na coluna "${card.colunaNome}"`}
          >
            Abrir no CRM · {card.quadroNome} › {card.colunaNome}
          </Link>
        ))
      )}
      {vincular.isError && <ErroDaApi titulo="Não foi possível vincular" erro={vincular.error} />}
    </div>
  );
}
